import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as api from "../../api/client";
import type {
  AdminNpcDeckSummary,
  AdminNpcSummary,
  DeckSummary,
} from "../../api/client";
import { formatChangesetErrors } from "../../logic/changesetErrors";
import { FACTIONS, type Faction } from "../../logic/deckBuilder";
import { slugify } from "../../logic/slug";
import { factionFor } from "../../theme/factions";
import { useResponsiveLayout } from "../../logic/useResponsiveLayout";
import { bevelShadow, defaultBtn, primaryBtn, withBtnStyle } from "../../theme/styles";
import { pixelTheme, type Theme } from "../../theme/pixel";

const ORPHAN_NPC_ID = "__orphan__";

interface OrphanGroup {
  npc_id: string;
  display_name: string;
  decks: AdminNpcDeckSummary[];
}

export function AdminNpcDecksTab() {
  const theme = pixelTheme;
  const { narrow } = useResponsiveLayout();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [npcs, setNpcs] = useState<AdminNpcSummary[]>([]);
  const [decks, setDecks] = useState<AdminNpcDeckSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // Admin's own decks for the per-row TEST PLAY picker. Loaded once on
  // mount; the picker is shared across all rows so changing it on one
  // row updates them all (same idiom as NewDeckTool's sandbox panel).
  const [userDecks, setUserDecks] = useState<DeckSummary[]>([]);
  const [chosenDeckId, setChosenDeckId] = useState<string>("");
  const [testPlayBusy, setTestPlayBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { npcs, decks } = await api.listAdminNpcDecks();
      setNpcs(npcs);
      setDecks(decks);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Load the admin's own deck list for the TEST PLAY picker. Best-
  // effort — if it fails, the per-row picker just shows "no decks"
  // and the rest of the tab keeps working.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { decks } = await api.listDecks();
        if (cancelled) return;
        setUserDecks(decks);
        if (decks.length > 0)
          setChosenDeckId((prev) => prev || String(decks[0].id));
      } catch {
        // Swallow — TEST PLAY rows will explain why.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onTestPlay = useCallback(
    async (npcDeckSlug: string) => {
      const chosen = userDecks.find((d) => String(d.id) === chosenDeckId);
      if (!chosen) {
        setError("Pick one of your decks first.");
        return;
      }
      setError(null);
      setTestPlayBusy(npcDeckSlug);
      try {
        const { game_id } = await api.startSandboxBattle({
          // Starter decks have negative virtual ids and need
          // `starter:<faction>`; only custom rows are `custom:<id>`.
          // `toDeckRef` picks the right shape from `is_starter`.
          deck: api.toDeckRef(chosen),
          opp: `npc:${npcDeckSlug}`,
        });
        navigate(`/battle/${game_id}`);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setTestPlayBusy(null);
      }
    },
    [chosenDeckId, userDecks, navigate],
  );

  // Group decks by npc_id; any deck whose npc_id isn't in the NPC
  // catalog is shown under a synthetic "Unassigned / orphaned" bucket
  // so admins can re-home it. Empty NPCs still appear so admins can
  // add their first deck.
  const groups = useMemo(() => {
    const npcIds = new Set(npcs.map((n) => n.id));
    const real: Array<{ npc: AdminNpcSummary; decks: AdminNpcDeckSummary[] }> =
      npcs.map((npc) => ({
        npc,
        decks: decks.filter((d) => d.npc_id === npc.id),
      }));
    const orphanDecks = decks.filter((d) => !npcIds.has(d.npc_id));
    const orphans: OrphanGroup[] = [];
    if (orphanDecks.length > 0) {
      const byId = new Map<string, AdminNpcDeckSummary[]>();
      for (const d of orphanDecks) {
        const key = d.npc_id || ORPHAN_NPC_ID;
        const arr = byId.get(key) ?? [];
        arr.push(d);
        byId.set(key, arr);
      }
      for (const [id, list] of byId) {
        orphans.push({
          npc_id: id,
          display_name: id === ORPHAN_NPC_ID ? "Unassigned" : `Orphaned · ${id}`,
          decks: list,
        });
      }
    }
    return { real, orphans };
  }, [npcs, decks]);

  const allNpcIds = useMemo(
    () => [
      ...groups.real.map((g) => g.npc.id),
      ...groups.orphans.map((g) => g.npc_id),
    ],
    [groups],
  );

  const selectedNpcId = useMemo(() => {
    const fromUrl = searchParams.get("npc");
    if (fromUrl && allNpcIds.includes(fromUrl)) return fromUrl;
    return allNpcIds[0] ?? null;
  }, [searchParams, allNpcIds]);

  function selectNpc(id: string) {
    const next = new URLSearchParams(searchParams);
    next.set("npc", id);
    setSearchParams(next, { replace: true });
    setCreating(false);
  }

  const selectedGroup = useMemo<SelectedGroup | null>(() => {
    if (!selectedNpcId) return null;
    const real = groups.real.find((g) => g.npc.id === selectedNpcId);
    if (real) {
      return {
        kind: "npc",
        npc: real.npc,
        decks: real.decks,
        canCreate: true,
      };
    }
    const orphan = groups.orphans.find((g) => g.npc_id === selectedNpcId);
    if (orphan) {
      return {
        kind: "orphan",
        npc_id: orphan.npc_id,
        display_name: orphan.display_name,
        decks: orphan.decks,
        canCreate: false,
      };
    }
    return null;
  }, [groups, selectedNpcId]);

  async function onResetChallenges(npcId: string) {
    const ok = window.confirm(
      `Reset YOUR OWN challenge progress for "${npcId}"?\n\nThis wipes every challenge_completion row for the calling admin against this NPC's challenge set so you can re-test the flow without waiting for the daily 08:00 UTC reset. Other users' progress is untouched.`,
    );
    if (!ok) return;
    try {
      const { deleted } = await api.resetAdminNpcChallenges(npcId);
      window.alert(
        deleted === 0
          ? "No completions to clear (already reset)."
          : `Cleared ${deleted} completion${deleted === 1 ? "" : "s"}.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function onDelete(deck: AdminNpcDeckSummary) {
    const ok = window.confirm(
      `Delete NPC deck "${deck.name}" (slug: ${deck.slug})?\n\nIf any challenge YAML or runtime code refers to this slug it will break.`,
    );
    if (!ok) return;
    try {
      await api.deleteAdminNpcDeck(deck.slug);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function onCreate(input: {
    npc_id: string;
    name: string;
    faction: Faction;
  }) {
    try {
      const { deck } = await api.createAdminNpcDeck({
        npc_id: input.npc_id,
        name: input.name,
        faction: input.faction,
        cards: [],
      });
      navigate(`/admin/npc-decks/${encodeURIComponent(deck.slug)}/edit`);
    } catch (err) {
      if (
        err instanceof api.ApiError &&
        err.body &&
        typeof err.body === "object" &&
        "details" in err.body
      ) {
        setError(formatChangesetErrors(err.body));
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    }
  }

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: narrow ? "minmax(0, 1fr)" : "minmax(260px, 320px) 1fr",
        gap: 16,
        flex: 1,
        minHeight: 0,
        overflowY: narrow ? "auto" : undefined,
      }}
    >
      <NpcList
        theme={theme}
        groups={groups}
        selectedNpcId={selectedNpcId}
        onSelect={selectNpc}
      />
      <div
        style={{
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          gap: 12,
          overflow: narrow ? undefined : "hidden",
        }}
      >
        {error && (
          <div
            style={{
              padding: 10,
              background: theme.panel,
              border: `2px solid ${theme.accent}`,
              color: theme.accent,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}
        {loading ? (
          <div style={{ color: theme.inkFaint }}>Loading…</div>
        ) : !selectedGroup ? (
          <div style={{ color: theme.inkFaint }}>
            No NPCs found. Add a YAML config under
            <code> card-engine/world/npcs/</code> to see one here.
          </div>
        ) : (
          <NpcDeckPanel
            onResetChallenges={onResetChallenges}
            theme={theme}
            group={selectedGroup}
            creating={creating}
            onStartCreate={() => setCreating(true)}
            onCancelCreate={() => setCreating(false)}
            onCreate={onCreate}
            onEdit={(slug) =>
              navigate(`/admin/npc-decks/${encodeURIComponent(slug)}/edit`)
            }
            onDelete={onDelete}
            userDecks={userDecks}
            chosenDeckId={chosenDeckId}
            setChosenDeckId={setChosenDeckId}
            onTestPlay={onTestPlay}
            testPlayBusy={testPlayBusy}
          />
        )}
      </div>
    </div>
  );
}

function NpcList({
  theme,
  groups,
  selectedNpcId,
  onSelect,
}: {
  theme: Theme;
  groups: {
    real: Array<{ npc: AdminNpcSummary; decks: AdminNpcDeckSummary[] }>;
    orphans: OrphanGroup[];
  };
  selectedNpcId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        minHeight: 0,
        overflowY: "auto",
      }}
    >
      <div
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 10,
          letterSpacing: theme.letterSpacing,
          color: theme.inkFaint,
        }}
      >
        NPCS
      </div>
      {groups.real.length === 0 && groups.orphans.length === 0 && (
        <div style={{ color: theme.inkFaint, fontSize: 13 }}>
          No NPCs configured.
        </div>
      )}
      {groups.real.map(({ npc, decks }) => (
        <NpcRow
          key={npc.id}
          theme={theme}
          active={selectedNpcId === npc.id}
          name={npc.name ?? npc.id}
          subtitle={
            npc.faction
              ? `${factionFor(npc.faction).name.toUpperCase()}${npc.role ? ` · ${npc.role}` : ""}`
              : npc.role || ""
          }
          deckCount={decks.length}
          onClick={() => onSelect(npc.id)}
        />
      ))}
      {groups.orphans.length > 0 && (
        <div
          style={{
            marginTop: 8,
            fontFamily: theme.fontDisplay,
            fontSize: 10,
            letterSpacing: theme.letterSpacing,
            color: theme.inkFaint,
          }}
        >
          UNASSIGNED
        </div>
      )}
      {groups.orphans.map((g) => (
        <NpcRow
          key={g.npc_id}
          theme={theme}
          active={selectedNpcId === g.npc_id}
          name={g.display_name}
          subtitle="No matching NPC config"
          deckCount={g.decks.length}
          onClick={() => onSelect(g.npc_id)}
        />
      ))}
    </div>
  );
}

function NpcRow({
  theme,
  active,
  name,
  subtitle,
  deckCount,
  onClick,
}: {
  theme: Theme;
  active: boolean;
  name: string;
  subtitle: string;
  deckCount: number;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        textAlign: "left",
        padding: 10,
        background: active ? theme.accent : theme.panel,
        color: active ? theme.bgDeep : theme.ink,
        border: `2px solid ${active ? theme.accent : theme.panelEdge}`,
        boxShadow: bevelShadow,
        cursor: "pointer",
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto",
        alignItems: "center",
        gap: 8,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 11,
            letterSpacing: theme.letterSpacing,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name.toUpperCase()}
        </div>
        {subtitle && (
          <div
            style={{
              fontSize: 11,
              color: active ? theme.bgDeep : theme.inkFaint,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {subtitle}
          </div>
        )}
      </div>
      <div
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 10,
          padding: "2px 6px",
          background: active ? theme.bgDeep : theme.panelSolid,
          color: active ? theme.accent : theme.inkDim,
          border: `1px solid ${active ? theme.bgDeep : theme.panelEdge}`,
        }}
      >
        {deckCount}
      </div>
    </button>
  );
}

type SelectedGroup =
  | {
      kind: "npc";
      npc: AdminNpcSummary;
      decks: AdminNpcDeckSummary[];
      canCreate: true;
    }
  | {
      kind: "orphan";
      npc_id: string;
      display_name: string;
      decks: AdminNpcDeckSummary[];
      canCreate: false;
    };

function NpcDeckPanel({
  theme,
  group,
  creating,
  onStartCreate,
  onCancelCreate,
  onCreate,
  onEdit,
  onDelete,
  onResetChallenges,
  userDecks,
  chosenDeckId,
  setChosenDeckId,
  onTestPlay,
  testPlayBusy,
}: {
  theme: Theme;
  group: SelectedGroup;
  creating: boolean;
  onStartCreate: () => void;
  onCancelCreate: () => void;
  onCreate: (input: {
    npc_id: string;
    name: string;
    faction: Faction;
  }) => void;
  onEdit: (slug: string) => void;
  onDelete: (deck: AdminNpcDeckSummary) => void;
  onResetChallenges: (npcId: string) => Promise<void>;
  userDecks: DeckSummary[];
  chosenDeckId: string;
  setChosenDeckId: (v: string) => void;
  onTestPlay: (slug: string) => void;
  testPlayBusy: string | null;
}) {
  const headerName =
    group.kind === "npc" ? (group.npc.name ?? group.npc.id) : group.display_name;
  const headerSub =
    group.kind === "npc"
      ? group.npc.faction
        ? factionFor(group.npc.faction).name.toUpperCase()
        : null
      : null;

  return (
    <div
      style={{
        background: theme.panel,
        border: `2px solid ${theme.panelEdge}`,
        boxShadow: bevelShadow,
        padding: 14,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div style={{ minWidth: 0, flex: "1 1 auto" }}>
          <div
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 14,
              letterSpacing: theme.letterSpacing,
              color: theme.ink,
            }}
          >
            {headerName.toUpperCase()}
          </div>
          {headerSub && (
            <div style={{ fontSize: 12, color: theme.inkFaint }}>
              {headerSub}
            </div>
          )}
        </div>
        {group.canCreate && (
          <button
            onClick={() => void onResetChallenges(group.npc.id)}
            title="Wipe your own challenge-completion rows for this NPC's challenge set, so you can re-test the flow without waiting for the daily reset."
            {...withBtnStyle(defaultBtn(theme), {
              fontSize: 11,
              padding: "8px 14px",
            })}
          >
            RESET CHALLENGES
          </button>
        )}
        {group.canCreate && !creating && (
          <button
            onClick={onStartCreate}
            {...withBtnStyle(primaryBtn(theme), {
              fontSize: 11,
              padding: "8px 14px",
            })}
          >
            + NEW DECK
          </button>
        )}
      </div>

      {creating && group.canCreate && (
        <NewDeckForm
          theme={theme}
          npcId={group.npc.id}
          defaultFaction={
            isFactionString(group.npc.faction) ? group.npc.faction : "humans"
          }
          onCancel={onCancelCreate}
          onCreate={onCreate}
        />
      )}

      {group.decks.length === 0 ? (
        <div style={{ color: theme.inkFaint, padding: "12px 4px" }}>
          {group.canCreate
            ? "No decks yet. Click + NEW DECK to create one."
            : "No decks under this group."}
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            overflowY: "auto",
            minHeight: 0,
          }}
        >
          {group.decks.map((d) => (
            <DeckRow
              key={d.slug}
              deck={d}
              theme={theme}
              onEdit={() => onEdit(d.slug)}
              onDelete={() => onDelete(d)}
              userDecks={userDecks}
              chosenDeckId={chosenDeckId}
              setChosenDeckId={setChosenDeckId}
              onTestPlay={() => onTestPlay(d.slug)}
              testPlayBusy={testPlayBusy === d.slug}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DeckRow({
  deck,
  theme,
  onEdit,
  onDelete,
  userDecks,
  chosenDeckId,
  setChosenDeckId,
  onTestPlay,
  testPlayBusy,
}: {
  deck: AdminNpcDeckSummary;
  theme: Theme;
  onEdit: () => void;
  onDelete: () => void;
  userDecks: DeckSummary[];
  chosenDeckId: string;
  setChosenDeckId: (v: string) => void;
  onTestPlay: () => void;
  testPlayBusy: boolean;
}) {
  const f = factionFor(deck.faction);
  const sizeIssue = deck.total !== 30;
  const hasUserDeck = userDecks.length > 0 && !!chosenDeckId;
  const sizeOk = deck.total === 30;
  // Disable test-play when either side isn't ready: no admin deck
  // picked, this NPC deck isn't 30 cards (engine will reject it), or
  // a previous test-play click is still in flight. A separate match
  // is force-ended before the new one starts (admin sandbox semantics),
  // so we only block while THIS row's request is in flight.
  const testPlayDisabled = testPlayBusy || !hasUserDeck || !sizeOk;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns:
          "auto minmax(0, 1fr) auto minmax(140px, 200px) auto auto auto",
        alignItems: "center",
        gap: 10,
        padding: "8px 10px",
        background: theme.panelSolid,
        border: `1px solid ${theme.panelEdge}`,
        borderLeft: `4px solid ${f.color}`,
      }}
    >
      <div
        title={f.name}
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 10,
          letterSpacing: theme.letterSpacing,
          padding: "2px 6px",
          background: f.color,
          color: theme.bgDeep,
        }}
      >
        {f.name.toUpperCase()}
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            color: theme.ink,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {deck.name}
        </div>
        <div
          style={{
            fontSize: 11,
            color: theme.inkFaint,
            fontFamily: "monospace",
          }}
        >
          {deck.slug}
        </div>
      </div>
      <div
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 11,
          color: sizeIssue ? theme.accent : theme.inkDim,
        }}
      >
        {deck.total}/30
      </div>
      {userDecks.length > 0 ? (
        <select
          value={chosenDeckId}
          onChange={(e) => setChosenDeckId(e.target.value)}
          title="Your deck for the test battle"
          style={{
            padding: "6px 8px",
            fontFamily: theme.fontBody,
            fontSize: 12,
            background: theme.panel,
            color: theme.ink,
            border: `2px solid ${theme.panelEdge}`,
            minWidth: 0,
          }}
        >
          {userDecks.map((d) => (
            <option key={d.id} value={String(d.id)}>
              {d.name} ({d.faction})
            </option>
          ))}
        </select>
      ) : (
        <span
          style={{ fontSize: 11, color: theme.inkFaint, fontStyle: "italic" }}
        >
          No decks
        </span>
      )}
      <button
        onClick={onTestPlay}
        disabled={testPlayDisabled}
        title={
          !hasUserDeck
            ? "Build a deck in the Deck Builder to test-play against this one."
            : !sizeOk
              ? "NPC deck must be exactly 30 cards before it can be played."
              : "Start a sandbox battle against this NPC deck."
        }
        {...withBtnStyle(defaultBtn(theme), {
          fontSize: 10,
          padding: "6px 10px",
          opacity: testPlayDisabled ? 0.5 : 1,
          cursor: testPlayDisabled ? "not-allowed" : "pointer",
        })}
      >
        {testPlayBusy ? "STARTING…" : "TEST PLAY"}
      </button>
      <button
        onClick={onEdit}
        {...withBtnStyle(defaultBtn(theme), {
          fontSize: 10,
          padding: "6px 10px",
        })}
      >
        EDIT
      </button>
      <button
        onClick={onDelete}
        {...withBtnStyle(defaultBtn(theme), {
          fontSize: 10,
          padding: "6px 10px",
          color: theme.accent,
        })}
      >
        DELETE
      </button>
    </div>
  );
}

function NewDeckForm({
  theme,
  npcId,
  defaultFaction,
  onCancel,
  onCreate,
}: {
  theme: Theme;
  npcId: string;
  defaultFaction: Faction;
  onCancel: () => void;
  onCreate: (input: {
    npc_id: string;
    name: string;
    faction: Faction;
  }) => void;
}) {
  const [name, setName] = useState("");
  const [faction, setFaction] = useState<Faction>(defaultFaction);
  const slug = useMemo(() => slugify(name), [name]);
  const canSubmit = name.trim().length > 0 && slug.length > 0;

  return (
    <div
      style={{
        background: theme.panelSolid,
        border: `1px dashed ${theme.panelEdge}`,
        padding: 12,
        display: "grid",
        gap: 10,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) auto",
          gap: 10,
          alignItems: "end",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 10,
              letterSpacing: theme.letterSpacing,
              color: theme.inkFaint,
            }}
          >
            DECK NAME
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Pip's Final Trick"
            maxLength={200}
            style={{
              padding: "8px 10px",
              fontFamily: theme.fontBody,
              fontSize: 14,
              background: theme.panel,
              color: theme.ink,
              border: `2px solid ${theme.panelEdge}`,
            }}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 10,
              letterSpacing: theme.letterSpacing,
              color: theme.inkFaint,
            }}
          >
            FACTION
          </label>
          <select
            value={faction}
            onChange={(e) => setFaction(e.target.value as Faction)}
            style={{
              padding: "8px 10px",
              fontFamily: theme.fontBody,
              fontSize: 14,
              background: theme.panel,
              color: theme.ink,
              border: `2px solid ${theme.panelEdge}`,
            }}
          >
            {FACTIONS.map((f) => (
              <option key={f} value={f}>
                {factionFor(f).name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div style={{ fontSize: 12, color: theme.inkFaint }}>
        Slug:{" "}
        <code style={{ color: slug ? theme.ink : theme.inkFaint }}>
          {slug || "(enter a name)"}
        </code>
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button
          onClick={onCancel}
          {...withBtnStyle(defaultBtn(theme), {
            fontSize: 11,
            padding: "8px 14px",
          })}
        >
          CANCEL
        </button>
        <button
          onClick={() =>
            onCreate({ npc_id: npcId, name: name.trim(), faction })
          }
          disabled={!canSubmit}
          {...withBtnStyle(primaryBtn(theme, !canSubmit), {
            fontSize: 11,
            padding: "8px 14px",
          })}
        >
          CREATE & EDIT
        </button>
      </div>
    </div>
  );
}

function isFactionString(s: string | null | undefined): s is Faction {
  return !!s && (FACTIONS as readonly string[]).includes(s);
}

